"use client";

import { CircularProgress } from "@mui/material";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";

import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import ListPriceForm from "@/components/templates/ListPriceForm";
import { buildFormSchema, buildInitialData } from "@/components/templates/ListPriceForm/form-data";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetListPriceById from "@/hooks/requests/list-price/use-get-list-price-by-id";
import useUpdateListPrice from "@/hooks/requests/list-price/use-update-list-price";
import { ListPriceFormPageMessages } from "@/messages/listPrice/list-price-form-page.messages";

import { ListPriceFormData } from "./types";

type Props = {
  messages: ListPriceFormPageMessages;
  isEditing?: boolean;
};

function ListPriceFormPage({ messages, isEditing }: Props) {
  const { listPriceId } = useParams();
  const router = useNavigate();
  const { lang } = useLanguage();
  const _id = Number(listPriceId) || 0;
  const { data, isLoading: isLoadingListPrice, error } = useGetListPriceById({ _id });
  const { update, isLoading: isLoadingUpdate } = useUpdateListPrice();

  const onSuccess = () => router(ROUTE_URLS.listPrice(lang));

  const handleSubmitForm = async (_formData: ListPriceFormData) => {
    await update({ formData: _formData, _id, onSuccess });
  };

  const isSuspended = isEditing ? isLoadingListPrice : false;
  const shouldLoadForm = isEditing ? !!data && !error : true;
  const shouldLoadError = !data && !!error && !isLoadingListPrice;

  const BREAD_CRUMBS = [{ title: messages.breadCrumbTitle, href: ROUTE_URLS.listPrice(lang) }];

  return (
    <PageLayout messages={messages} title={messages.updatePageTitle} breadCrumbs={BREAD_CRUMBS}>
      <PageContainer>
        <Suspense fallback={<CircularProgress />} isSuspended={isSuspended}>
          {shouldLoadForm && (
            <Formik
              initialValues={buildInitialData(data)}
              validationSchema={buildFormSchema(messages)}
              validateOnChange={false}
              onSubmit={handleSubmitForm}
            >
              <ListPriceForm messages={messages} isLoading={isLoadingUpdate} />
            </Formik>
          )}
          {shouldLoadError && (
            <AlertBlock
              title={messages.salesPriceListErrorToLoad}
              description={error?.message || ""}
              alt={messages.salesPriceListNotFoundAlt}
            />
          )}
        </Suspense>
      </PageContainer>
    </PageLayout>
  );
}

export default ListPriceFormPage;
